import React, { useEffect, useRef, useState } from 'react';

/* ── Install Prompt (Android Chrome shows a beforeinstallprompt event) ── */
export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('pwa-install-dismissed') === 'true'
  );
  const dismissedRef = useRef(dismissed);

  useEffect(() => {
    // The event may have already fired before this component mounted —
    // index.js captures it in window.__pwaInstallPrompt for exactly this case.
    if (window.__pwaInstallPrompt && !dismissedRef.current) {
      setDeferredPrompt(window.__pwaInstallPrompt);
      setShow(true);
    }

    const handler = (e) => {
      e.preventDefault();
      window.__pwaInstallPrompt = e;
      setDeferredPrompt(e);
      if (!dismissedRef.current) setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Hide once installed
  useEffect(() => {
    const handler = () => setShow(false);
    window.addEventListener('appinstalled', handler);
    return () => window.removeEventListener('appinstalled', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setShow(false);
    setDeferredPrompt(null);
    window.__pwaInstallPrompt = null;
  };

  const handleDismiss = () => {
    setShow(false);
    setDismissed(true);
    dismissedRef.current = true;
    window.__pwaInstallPrompt = null;
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 80, left: 16, right: 16, zIndex: 9999,
      background: '#0a1628', color: '#fff', borderRadius: 16,
      padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14,
      boxShadow: '0 8px 32px rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)',
      animation: 'pwa-slide-up 0.35s ease',
      maxWidth: 480, margin: '0 auto',
    }}>
      <img src="/prasilabs-logo.png" alt="PRASI CRM"
        style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'contain', background: '#fff', padding: 4, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: '0.9rem', marginBottom: 2 }}>Install PRASI CRM</div>
        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.4 }}>
          Add to your home screen for quick access — works offline too.
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
        <button onClick={handleInstall}
          style={{ padding: '7px 16px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Install
        </button>
        <button onClick={handleDismiss}
          style={{ padding: '5px 12px', background: 'transparent', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, fontSize: '0.75rem', cursor: 'pointer' }}>
          Not now
        </button>
      </div>
      <style>{`
        @keyframes pwa-slide-up {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

/* ── Update Available Banner (shown when new SW is waiting) ── */
export function PWAUpdateBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const handler = () => setShow(true);
    window.addEventListener('sw-update-available', handler);
    return () => window.removeEventListener('sw-update-available', handler);
  }, []);

  const handleUpdate = () => {
    // Tell the waiting SW to skip waiting and take control.
    // The serviceWorkerRegistration controllerchange listener handles the reload.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(reg => {
        if (reg.waiting) {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        } else {
          window.location.reload();
        }
      });
    } else {
      window.location.reload();
    }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
      background: '#1d4ed8', color: '#fff',
      padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, fontSize: '0.85rem', boxShadow: '0 4px 12px rgba(29,78,216,0.4)',
    }}>
      <span>🚀 A new version of PRASI CRM is available.</span>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button onClick={handleUpdate}
          style={{ padding: '6px 16px', background: '#fff', color: '#1d4ed8', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
          Update Now
        </button>
        <button onClick={() => setShow(false)}
          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: '1rem', padding: '0 4px' }}>
          ✕
        </button>
      </div>
    </div>
  );
}

/* ── Offline Banner ── */
export function OfflineBanner() {
  const [offline, setOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline  = () => setOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online',  goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online',  goOnline);
    };
  }, []);

  if (!offline) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9998,
      background: '#92400e', color: '#fff',
      padding: '9px 20px', textAlign: 'center',
      fontSize: '0.82rem', fontWeight: 600,
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    }}>
      📵 You're offline — some features may be unavailable. Data will sync when you reconnect.
    </div>
  );
}
